import { DeleteCardLink, UpdateCardLink } from '@/services/cardLinkService';
import {
  CreateCard,
  CreateCardLink,
  DeleteCard,
  DeleteCardImage,
  LinkAccessoryToCard,
  ListCards,
  ShowCard,
  ShowPublicCard,
  UpdateCard,
  UpdateCardImage,
  UpdateCardLinks,
  UpdateCardQrCodeColor,
  deleteFollowUpAttachment,
  listFollowUpAttachments,
  testFollowUpMail,
  uploadFollowUpAttachment,
} from '@/services/cardService';
import { Card, CardLink } from '@/types/card';
import { CardLinkFormValues } from '@/types/cardLink';
import { Query } from '@/types/query';
import { PaginationData, Response } from '@/types/response';
import { handleError } from '@/utils/handleError';
import toast from 'react-hot-toast';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface FileAttachment {
  name: string;
  url: string;
  size: number;
}

export interface CardState {
  selectedMenuItem: number;
  card: Card | null;
  cards: Card[];
  loading: boolean;
  pagination: PaginationData;
  isFetching: boolean;
  limitExceeded: boolean;
  showCard: (slug: string) => Promise<void>;
  listCards: (query: Query) => Promise<void>;
  createCard: (userId: number, accessoryUuid?: string) => Promise<Card>;
  setSelectedMenuItem: (id: number) => Promise<void>;
  updateCard: (
    slug: string,
    card: Card,
  ) => Promise<{ data: Card; message: string }>;
  deleteCardImage: (
    slug: string,
    type: 'avatar' | 'company_logo' | 'cover',
  ) => Promise<void>;
  updateCardImage: (
    slug: string,
    type: 'avatar' | 'company_logo' | 'cover',
    image: string,
  ) => Promise<void>;
  linkAccessoryToCard: (accessoryUuid: string, slug: string) => Promise<void>;
  updateCardLinks: (slug: string, links: CardLink[]) => Promise<void>;
  updateCardLink: (
    id: number,
    values: Partial<CardLink>,
  ) => Promise<Response<Card>>;
  createCardLink: (
    slug: string,
    values: CardLinkFormValues,
    isCustomLink: boolean,
  ) => Promise<Response<Card>>;
  deleteCardLink: (id: number) => Promise<void>;
  updateCardQrCodeColor: (slug: string, color: string) => Promise<void>;
  deleteCard: (slug: string) => Promise<void>;
  showPublicCard: (slug: string) => Promise<void>;
  followUpAttachments: FileAttachment[];
  fetchFollowUpAttachments: (cardId: number) => Promise<void>;
  uploadFollowUpAttachment: (cardId: number, file: File) => Promise<void>;
  deleteFollowUpAttachment: (cardId: number, filename: string) => Promise<void>;
  testFollowUpMail: (cardId: number, email: string) => Promise<void>;
}

export const useCardStore = create<CardState>()(
  immer<CardState>((set, get) => ({
    selectedMenuItem: 1,
    card: null,
    cards: [],
    loading: false,
    isFetching: false,
    limitExceeded: false,
    pagination: {
      total: 0,
      per_page: 10,
      current_page: 1,
      last_page: 1,
    },
    followUpAttachments: [],

    showCard: async (slug: string) => {
      try {
        set(state => {
          state.isFetching = true;
        });
        const { data } = await ShowCard(slug);
        set(state => {
          state.card = data;
        });
      } catch (error) {
        if (
          (error as ApiError).response?.data?.message === 'Resource not found'
        ) {
          set(state => {
            state.card = null;
          });
        } else {
          handleError(error);
        }
      } finally {
        set(state => {
          state.isFetching = false;
        });
      }
    },

    listCards: async query => {
      try {
        set(state => {
          state.isFetching = true;
        });

        const { data } = await ListCards(query);

        if ('pagination' in data) {
          set(state => {
            if (query.current_page === 1) {
              state.cards = data.data;
            } else {
              state.cards.push(...data.data);
            }
            state.pagination = data.pagination;
            state.limitExceeded = data.meta.limit_exceeded;
          });
        } else {
          set(state => {
            state.cards = data;
            state.pagination = {
              total: data.length,
              per_page: data.length,
              current_page: 1,
              last_page: 1,
            };

            state.limitExceeded = false;
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.isFetching = false;
        });
      }
    },

    createCard: async (userId: number, accessoryUuid?: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await CreateCard(userId, accessoryUuid);
        set(state => {
          state.card = data;
        });
        return data;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCard: async (slug: string, card: Card) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await UpdateCard(slug, card);
        set(state => {
          state.card = data;
        });
        return { data, message };
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    setSelectedMenuItem: async (id: number) => {
      set(state => {
        state.selectedMenuItem = id;
      });
    },

    deleteCardImage: async (
      slug: string,
      type: 'avatar' | 'company_logo' | 'cover',
    ) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await DeleteCardImage(slug, type);
        toast.success(message);
        set(state => {
          state.card = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCardImage: async (
      slug: string,
      type: 'avatar' | 'company_logo' | 'cover',
      image: string,
    ) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await UpdateCardImage(slug, type, image);
        toast.success(message);
        set(state => {
          state.card = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    linkAccessoryToCard: async (accessoryUuid: string, slug: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await LinkAccessoryToCard(
          accessoryUuid,
          slug,
        );
        toast.success(message);
        set(state => {
          state.card = data;
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCardLinks: async (slug: string, links: CardLink[]) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await UpdateCardLinks(slug, links);
        set(state => {
          state.card = data;
        });
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCardLink: async (id: number, values: Partial<CardLink>) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await UpdateCardLink(id, values);
        set(state => {
          state.card = data;
        });
        return { data, message };
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    createCardLink: async (
      slug: string,
      values: CardLinkFormValues,
      isCustomLink: boolean,
    ) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await CreateCardLink(
          slug,
          values,
          isCustomLink,
        );
        toast.success(message);
        set(state => {
          state.card = data;
        });
        return { data, message };
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteCardLink: async (id: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await DeleteCardLink(id);
        set(state => {
          state.card = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateCardQrCodeColor: async (slug: string, color: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await UpdateCardQrCodeColor(slug, color);
        set(state => {
          state.card = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteCard: async (slug: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { message } = await DeleteCard(slug);
        toast.success(message);

        set(state => {
          state.cards = state.cards.filter(card => card.slug !== slug);
          state.card = null;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    showPublicCard: async (slug: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await ShowPublicCard(slug);
        set(state => {
          state.card = data;
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    fetchFollowUpAttachments: async (cardId: number) => {
      try {
        const res = await listFollowUpAttachments(cardId);
        set(state => {
          state.followUpAttachments = res.data || [];
        });
      } catch {
        set(state => {
          state.followUpAttachments = [];
        });
      }
    },

    uploadFollowUpAttachment: async (cardId: number, file: File) => {
      await uploadFollowUpAttachment(cardId, file);
      await get().fetchFollowUpAttachments(cardId);
    },

    deleteFollowUpAttachment: async (cardId: number, filename: string) => {
      await deleteFollowUpAttachment(cardId, filename);
      await get().fetchFollowUpAttachments(cardId);
    },

    testFollowUpMail: async (cardId: number, email: string) => {
      await testFollowUpMail(cardId, email);
    },
  })),
);
